import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Alert, Button, Card, Col, Form, Image, InputGroup, ListGroup, Row, Badge, OverlayTrigger, Tooltip, Spinner, Modal } from 'react-bootstrap';
import {
    FaArrowRight,
    FaCheckCircle,
    FaChevronLeft,
    FaChevronRight,
    FaInfoCircle,
    FaLock,
    FaShareAlt,
    FaShoppingCart,
    FaCheck,
    FaTrash,
} from 'react-icons/fa';
import { FaTag } from 'react-icons/fa6';
import '../../assets/styles/action-feedback.css';
import { feedbackClassName, useKeyedActionFeedback } from '../../hooks/useActionFeedback';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import Message from '../../components/layout/Message';
import Loader from '../../components/layout/Loader';
import Meta from '../../components/layout/Meta';
import {
    useGetCartQuery,
    useRemoveFromCartMutation,
    useUpdateCartItemMutation,
    useUpdateCartItemVariationMutation,
    useValidateCartStockMutation,
    useApplyDiscountToCartMutation,
    useRemoveDiscountFromCartMutation,
    useCreateSharedCartMutation,
    useClearCartItemsByCompanyMutation,
} from '../../slices/cartApiSlice';
import { useGetProductDetailsQuery } from '../../slices/productApiSlice';
import { setSelectedCompany, selectActiveOrder, selectSelectedCompanyRuc } from '../../slices/cartSlice';
import ProductPrice from '../../components/product/ProductPrice';
import QuantitySelector from '../../components/ui/QuantitySelector';
import TotalPrice from '../../components/product/TotalPrice';
import ContificoCodeBadge from '../../components/ui/ContificoCodeBadge';
import useIsMobile from '../../utils/useIsMobile';
import CartProductUrgency from '../../components/product/CartProductUrgency';
import ActiveGlobalDiscountLine from '../../components/checkout/ActiveGlobalDiscountLine';
import OrderPriceSummary from '../../components/checkout/OrderPriceSummary';
import ShippingBehaviorBanner from '../../components/ui/ShippingBehaviorBanner';
import { FreeShippingBadge, ProductRowBenefitNotes } from '../../components/product/ProductBenefitMarks';
import ApplyGlobalDiscountButton from '../../components/checkout/ApplyGlobalDiscountButton';
import useFetchActiveGlobalDiscount from '../../hooks/useFetchActiveGlobalDiscount';
import { selectActiveGlobalDiscount } from '../../slices/activeGlobalDiscountSlice';
import {
    GLOBAL_DISCOUNT_BELOW_MINIMUM_MESSAGE,
    appliedGlobalDiscountBelowMinimum,
    discountMeetsMinimumPurchase,
    discountSummaryLabel,
    discountsForCompany,
    findGlobalDiscountByCode,
    getApplicableGlobalDiscount,
    resolveSummaryDiscount,
} from '../../utils/activeGlobalDiscount';
import { isLinkedVariation } from '../../utils/productUtils';
import { getExTaxItemsSubtotal, totalsWithoutShipping } from '../../utils/priceUtils';

import 'swiper/css';
import 'swiper/css/pagination';
import '../../assets/styles/cart.screen.css';

const CartVariationSelector = ({ item, onChange, disabled }) => {
    const { data: productDetails, isLoading } = useGetProductDetailsQuery(
        item.product,
        { skip: !item.product }
    );

    const hasLinkedProduct = (option) => {
        if (typeof option?.linkedProduct === 'string') {
            return option.linkedProduct.trim().length > 0;
        }
        return Boolean(option?.linkedProduct);
    };

    const handleVariationSelect = (variationType, optionName) => {
        if (disabled || !productDetails) return;

        const variationTypeConfig = productDetails.variations.find(v => v.variationType === variationType);
        const selectableOptions = (variationTypeConfig?.options || []).filter((option) => !hasLinkedProduct(option));
        const selectedOption = selectableOptions.find(o => o.name === optionName);

        if (!selectedOption) return;

        const newVariation = {
            variationType: variationType,
            option: selectedOption.name,
        };

        onChange(item._id, newVariation);
    };

    if (isLoading) {
        return <div className="small text-muted py-1">Cargando...</div>;
    }

    if (!productDetails || !productDetails.variations || productDetails.variations.length === 0) {
        if (item.variation?.option) {
            return (
                <div className="small mt-1 text-muted">
                    <span className="fw-bold">{item.variation.variationType}:</span> {item.variation.option}
                </div>
            );
        }
        return null;
    }

    const visibleVariations = productDetails.variations
        .filter((variation) => !isLinkedVariation(variation))
        .map((variation) => ({
            ...variation,
            options: (variation.options || []).filter((option) => !hasLinkedProduct(option)),
        }))
        .filter((variation) => variation.options.length > 0);

    if (visibleVariations.length === 0) {
        return null;
    }

    return (
        <div className="mt-1">
            {visibleVariations.map((v) => (
                <div key={v.variationType} className="mb-1 d-flex align-items-center">
                    <span className="small fw-bold me-2 text-nowrap">{v.variationType}:</span>
                    <Form.Control
                        as="select"
                        size="sm"
                        className="p-1"
                        style={{ fontSize: '0.75rem', height: 'auto' }}
                        value={item.variation?.option || ''}
                        onChange={(e) => handleVariationSelect(v.variationType, e.target.value)}
                        disabled={disabled}
                    >
                        {v.options.map((opt) => (
                            <option key={opt.name} value={opt.name}>
                                {opt.name}
                            </option>
                        ))}
                    </Form.Control>
                </div>
            ))}
        </div>
    );
};


const CartDistributorStockInfo = ({ productId }) => {
    const { data: productDetails, isLoading } = useGetProductDetailsQuery(
        productId,
        { skip: !productId }
    );

    if (isLoading || !productDetails) {
        return null;
    }

    const countInStock = productDetails.countInStock || 0;
    const unitsPerBox = productDetails.unitsPerBox || 0;
    const showUnitsPerBox = Number.isFinite(unitsPerBox) && unitsPerBox > 0 && countInStock >= unitsPerBox * 1.5;

    return (
        <div className="cart-distributor-info-row mt-1" style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '0.85rem' }}>
            <div className="cart-stock-info" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ fontWeight: '600', color: '#495057' }}>Stock:</span>
                    <span style={{ fontWeight: '600', color: '#212529' }}>{countInStock}</span>
                </div>
                <div
                    style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor:
                            countInStock > 50 ? '#28a745' :
                            countInStock > 25 ? '#ffc107' :
                            countInStock > 0 ? '#fd7e14' : '#dc3545'
                    }}
                />
            </div>

            {showUnitsPerBox && (
                <div className="cart-units-info" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ fontWeight: '600', color: '#495057' }}>Caja:</span>
                        <span style={{ fontWeight: '600', color: '#212529' }}>{unitsPerBox}</span>
                    </div>
                    <span style={{ fontSize: '1rem' }}>📦</span>
                </div>
            )}
        </div>
    );
};


const CartScreen = () => {
    const dispatch = useDispatch();
    useFetchActiveGlobalDiscount();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const swiperRef = useRef(null);
    const mobileFooterRef = useRef(null);

    const { userInfo } = useSelector((state) => state.auth);
    const activeOrder = useSelector(selectActiveOrder);
    const selectedCompanyRuc = useSelector(selectSelectedCompanyRuc);
    const cart = useSelector((state) => state.cart?.activeOrder);
    const { companiesByRuc } = useSelector((state) => state.companies);

    const { isLoading, error } = useGetCartQuery();

    const [removeCartItem] = useRemoveFromCartMutation();
    const [updateCartItem] = useUpdateCartItemMutation();
    const [updateCartItemVariation] = useUpdateCartItemVariationMutation();
    const [validateCartStock, { isLoading: isValidatingStock }] = useValidateCartStockMutation();
    const [applyDiscountToCart, { isLoading: loadingCoupon }] = useApplyDiscountToCartMutation();
    const [removeDiscountFromCart, { isLoading: removingCoupon }] = useRemoveDiscountFromCartMutation();
    const [createSharedCart, { isLoading: isCreatingSharedCart }] = useCreateSharedCartMutation();
    const [clearCompanyCart] = useClearCartItemsByCompanyMutation();

    const [stockErrors, setStockErrors] = useState([]);
    const [isStockValidated, setIsStockValidated] = useState(false);
    const [stockValidationFailed, setStockValidationFailed] = useState(false);
    const [stockValidationMessage, setStockValidationMessage] = useState('');
    const [discountCode, setDiscountCode] = useState('');
    const [discountApplyError, setDiscountApplyError] = useState('');
    const [globalDiscountError, setGlobalDiscountError] = useState('');
    const [applyingGlobalDiscount, setApplyingGlobalDiscount] = useState(false);
    
    const [showSwipeHint, setShowSwipeHint] = useState(false);
    const [showMobileFooterDetails, setShowMobileFooterDetails] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [confirmEmptyRuc, setConfirmEmptyRuc] = useState(null);
    const [emptyingRuc, setEmptyingRuc] = useState(null);
    const confirmEmptyTimerRef = useRef(null);
    const [sharedCartLink, setSharedCartLink] = useState('');
    const [copiedShareLink, setCopiedShareLink] = useState(false);
    const { statusByKey: actionFeedback, succeed: flashActionSuccess, fail: flashActionError } = useKeyedActionFeedback();
    const welcomeDiscountRef = useRef(null);
    const previousGlobalDiscountRef = useRef(null);
    const globalDiscountRemovalAttemptRef = useRef('');

    const companies = activeOrder?.companies || [];
    const companiesArray = Array.isArray(companies) ? companies : Object.values(companies);
    const allItems = companiesArray.flatMap(c => c.orderItems || c.items || []);

    // Use useCallback to prevent function recreation on every render
    const handleQtyChange = useCallback(async (item, qty) => {
        if (!item || !item._id) {
            console.error("Invalid item for qty update:", item);
            return;
        }
        try {
            await updateCartItem({ itemId: item._id, qty }).unwrap();
            flashActionSuccess(`qty:${item._id}`);
            // Invalidate stock validation on change
            setIsStockValidated(false);
            setStockValidationFailed(false);
            setStockValidationMessage('');
        } catch {
            flashActionError(`qty:${item._id}`);
        }
    }, [updateCartItem, flashActionSuccess, flashActionError]);

    const handleRemoveItem = useCallback(async (itemId) => {
        if (!itemId) return;
        try {
            await removeCartItem(itemId).unwrap();
            flashActionSuccess(`remove:${itemId}`);
            // Invalidate stock validation on change
            setIsStockValidated(false);
            setStockValidationFailed(false);
            setStockValidationMessage('');
        } catch {
            flashActionError(`remove:${itemId}`);
        }
    }, [removeCartItem, flashActionSuccess, flashActionError]);

    const handleVariationChange = useCallback(async (itemId, newVariation) => {
        if (!itemId) return;
        try {
            await updateCartItemVariation({ itemId, variation: newVariation }).unwrap();
            flashActionSuccess(`variation:${itemId}`);
            // Invalidate stock validation on change
            setIsStockValidated(false);
            setStockValidationFailed(false);
            setStockValidationMessage('');
        } catch {
            flashActionError(`variation:${itemId}`);
        }
    }, [updateCartItemVariation, flashActionSuccess, flashActionError]);

    const checkoutHandler = () => {
        if (!selectedCompanyRuc) {
            flashActionError('checkout');
            return;
        }
        if (selectedGlobalDiscountBelowMinimum) {
            setGlobalDiscountError(GLOBAL_DISCOUNT_BELOW_MINIMUM_MESSAGE);
            return;
        }
        navigate('/login?redirect=/shipping');
    };

    const handleEmptyCompanyCart = useCallback(async (companyRuc) => {
        if (!companyRuc || emptyingRuc) {
            return;
        }

        if (confirmEmptyRuc !== companyRuc) {
            setConfirmEmptyRuc(companyRuc);
            if (confirmEmptyTimerRef.current) {
                window.clearTimeout(confirmEmptyTimerRef.current);
            }
            confirmEmptyTimerRef.current = window.setTimeout(() => {
                setConfirmEmptyRuc(null);
                confirmEmptyTimerRef.current = null;
            }, 3200);
            return;
        }

        setConfirmEmptyRuc(null);
        if (confirmEmptyTimerRef.current) {
            window.clearTimeout(confirmEmptyTimerRef.current);
            confirmEmptyTimerRef.current = null;
        }

        setEmptyingRuc(companyRuc);
        try {
            await clearCompanyCart(companyRuc).unwrap();
            flashActionSuccess(`empty:${companyRuc}`);
            setIsStockValidated(false);
            setStockValidationFailed(false);
            setStockValidationMessage('');
        } catch {
            flashActionError(`empty:${companyRuc}`);
        } finally {
            setEmptyingRuc(null);
        }
    }, [
        emptyingRuc,
        confirmEmptyRuc,
        clearCompanyCart,
        flashActionSuccess,
        flashActionError,
    ]);

    const handleShareCompanyCart = useCallback(async (companyRuc) => {
        if (!companyRuc) return;
        try {
            const result = await createSharedCart(companyRuc).unwrap();
            const token = result?.token;
            if (!token) {
                flashActionError(`share:${companyRuc}`);
                return;
            }
            setSharedCartLink(`${window.location.origin}/shared-cart/${token}`);
            setShowShareModal(true);
            flashActionSuccess(`share:${companyRuc}`);
        } catch {
            flashActionError(`share:${companyRuc}`);
        }
    }, [createSharedCart, flashActionSuccess, flashActionError]);

    const handleCopyShareLink = useCallback(async () => {
        if (!sharedCartLink) return;
        try {
            await navigator.clipboard.writeText(sharedCartLink);
            setCopiedShareLink(true);
            flashActionSuccess('copy');
            window.setTimeout(() => setCopiedShareLink(false), 1800);
        } catch {
            flashActionError('copy');
        }
    }, [sharedCartLink, flashActionSuccess, flashActionError]);

    const handleApplyDiscount = async (e) => {
        e.preventDefault();
        setDiscountApplyError('');

        if (appliedCoupon?.code) {
            setDiscountApplyError('Ya tienes un cupón aplicado. Quítalo para ingresar otro.');
            return;
        }

        const code = discountCode.trim();
        if (!code || !selectedCompanyRuc) {
            setDiscountApplyError('Error al aplicar el cupón.');
            return;
        }

        try {
            await applyDiscountToCart({ code, companyRuc: selectedCompanyRuc }).unwrap();
            setDiscountCode('');
        } catch (err) {
            if (err.status === 401) {
                setDiscountApplyError('Debes iniciar sesión para aplicar descuentos.');
            } else {
                setDiscountApplyError(err?.data?.message || 'Error al aplicar el cupón.');
            }
        }
    };

    const handleRemoveDiscount = async () => {
        setDiscountApplyError('');

        if (!selectedCompanyRuc) {
            setDiscountApplyError('No se pudo quitar el cupón.');
            return;
        }

        try {
            await removeDiscountFromCart({ companyRuc: selectedCompanyRuc }).unwrap();
            setDiscountCode('');
        } catch (err) {
            if (err.status === 401) {
                setDiscountApplyError('Debes iniciar sesión para quitar descuentos.');
            } else {
                setDiscountApplyError(err?.data?.message || 'No se pudo quitar el cupón.');
            }
        }
    };

    useEffect(() => {
        const availableCompanies = companiesArray.filter(c => !c.isPaid && !c.checkoutLocked);
        const availableRucs = availableCompanies.map(c => c.company);

        if (availableRucs.length > 0) {
            if (!selectedCompanyRuc || !availableRucs.includes(selectedCompanyRuc)) {
                dispatch(setSelectedCompany(availableRucs[0]));
            }
        } else if (selectedCompanyRuc) {
             const isStillAvailable = availableRucs.includes(selectedCompanyRuc);
             if (!isStillAvailable) {
                 // Logic to handle if selected company becomes unavailable
             }
        }
    }, [companiesArray, selectedCompanyRuc, dispatch]);

    // Sync Swiper with selectedCompanyRuc if changed externally (or initially)
    useEffect(() => {
        if (swiperRef.current && selectedCompanyRuc) {
            const index = companiesArray.findIndex(c => c.company === selectedCompanyRuc);
            if (index !== -1 && swiperRef.current.activeIndex !== index) {
                swiperRef.current.slideTo(index);
            }
        }
    }, [selectedCompanyRuc, companiesArray]);

    // Move selectedCompany and selectedCompanyItems above this effect, and useMemo for items
    const selectedCompany = companiesArray.find(c => c.company === selectedCompanyRuc);
    const selectedCompanyItems = useMemo(() => {
        return selectedCompany?.orderItems || selectedCompany?.items || [];
    }, [selectedCompany]);

    // Validate stock on load, when company changes, or when cart items change
    useEffect(() => {
        const validateStock = async () => {
            if (selectedCompanyRuc && !isStockValidated) {
                try {
                    const res = await validateCartStock({ companyId: selectedCompanyRuc }).unwrap();
                    if (res.ok || res.success) {
                        setStockErrors([]);
                        setStockValidationFailed(false);
                        setStockValidationMessage('');
                    } else {
                        setStockErrors(res.unavailableItems || []);
                        setStockValidationFailed(true);
                        setStockValidationMessage(res.message || 'Stock validation failed. Please review your cart.');
                    }
                    setIsStockValidated(true);
                } catch (err) {
                    // FIX: Extract unavailableItems from error response if present
                    const unavailable = err?.data?.unavailableItems || [];
                    setStockErrors(unavailable);
                    setStockValidationFailed(true);
                    setStockValidationMessage(
                        err?.data?.message || err?.message || 'An error occurred while validating stock.'
                    );
                }
            }
        };

        validateStock();
    }, [selectedCompanyRuc, validateCartStock, isStockValidated, selectedCompanyItems]);

    const handleSlideChange = (swiper) => {
        if (showSwipeHint) {
            dismissSwipeHint();
        }

        const activeIndex = swiper.activeIndex;
        const company = companiesArray[activeIndex];
        if (company) {
            if (!company.isPaid && !company.checkoutLocked) {
                dispatch(setSelectedCompany(company.company));
                setIsStockValidated(false); // Re-validate for new company
            } else {
                dispatch(setSelectedCompany(null));
            }
        }
    };

    const appliedCoupon = cart?.appliedCoupon || selectedCompany?.appliedCoupon;
    const hasAppliedDiscount = Boolean(appliedCoupon?.code);
    const taxPrice = Number(selectedCompany?.taxPrice ?? cart?.taxPrice ?? 0);
    const totalPrice = Number(selectedCompany?.totalPrice ?? cart?.totalPrice ?? 0);
    const selectedShippingPrice = Number(
        selectedCompany?.shippingPrice ?? cart?.shippingPrice ?? 0
    );
    const discountAmount = Number(
        selectedCompany?.discountAmount ??
        selectedCompany?.couponDiscountAmount ??
        cart?.discountAmount ??
        cart?.couponDiscountAmount ??
        0
    );
    const preDiscountItemsPrice = getExTaxItemsSubtotal(
        selectedCompanyItems.length > 0 ? selectedCompanyItems : allItems
    );
    const activeGlobalDiscount = useSelector(selectActiveGlobalDiscount);
    const applicableGlobalDiscount = useMemo(() => {
        if (!userInfo || !selectedCompanyRuc) {
            return null;
        }

        return getApplicableGlobalDiscount(
            discountsForCompany(activeGlobalDiscount, selectedCompanyRuc),
            {
                appliedCode: appliedCoupon?.code,
                itemsSubtotal: preDiscountItemsPrice,
            }
        );
    }, [
        userInfo,
        selectedCompanyRuc,
        activeGlobalDiscount,
        appliedCoupon?.code,
        preDiscountItemsPrice,
    ]);
    const selectedCompanyName = companiesByRuc?.[selectedCompanyRuc]?.tradeName || '';
    if (applicableGlobalDiscount) {
        welcomeDiscountRef.current = applicableGlobalDiscount;
    }
    const welcomeButtonDiscount = applicableGlobalDiscount
        || (actionFeedback.welcome === 'success' ? welcomeDiscountRef.current : null);
    const selectedGlobalDiscountBelowMinimum = appliedGlobalDiscountBelowMinimum({
        discounts: discountsForCompany(activeGlobalDiscount, selectedCompanyRuc),
        appliedCoupon,
        itemsSubtotal: preDiscountItemsPrice,
    });
    const globalDiscountRemovalKey = companiesArray
        .map((companyOrder) => {
            const companyRuc = companyOrder?.company;
            if (!companyRuc || companyOrder.isPaid || companyOrder.checkoutLocked) {
                return '';
            }

            const coupon = companyRuc === selectedCompanyRuc
                ? (appliedCoupon || companyOrder.appliedCoupon || null)
                : (companyOrder.appliedCoupon || null);
            const items = companyOrder.orderItems || companyOrder.items || [];
            const belowMinimum = appliedGlobalDiscountBelowMinimum({
                discounts: discountsForCompany(activeGlobalDiscount, companyRuc),
                appliedCoupon: coupon,
                itemsSubtotal: getExTaxItemsSubtotal(items),
            });
            if (!belowMinimum || !coupon?.code) {
                return '';
            }

            return `${companyRuc}:${String(coupon.code).trim().toUpperCase()}`;
        })
        .filter(Boolean)
        .join('|');
    const summaryDiscount = resolveSummaryDiscount({
        activeGlobalDiscount,
        companyRuc: selectedCompanyRuc,
        appliedCoupon,
    });
    const discountLabel = discountSummaryLabel(summaryDiscount);
    const selectedCartTotals = totalsWithoutShipping({
        totalPrice,
        taxPrice,
        shippingPrice: selectedShippingPrice,
    });
    const globalDiscountNotice = selectedGlobalDiscountBelowMinimum
        ? GLOBAL_DISCOUNT_BELOW_MINIMUM_MESSAGE
        : globalDiscountError;

    const handleApplyGlobalDiscount = async () => {
        setGlobalDiscountError('');
        setDiscountApplyError('');

        if (!userInfo) {
            navigate('/login?redirect=/cart');
            return;
        }

        if (!applicableGlobalDiscount?.code || !selectedCompanyRuc || applyingGlobalDiscount) {
            return;
        }

        if (!discountMeetsMinimumPurchase(applicableGlobalDiscount, preDiscountItemsPrice)) {
            setGlobalDiscountError('La compra no alcanza el monto mínimo de este descuento.');
            return;
        }

        setApplyingGlobalDiscount(true);
        try {
            await applyDiscountToCart({
                code: applicableGlobalDiscount.code,
                companyRuc: selectedCompanyRuc,
            }).unwrap();
            setDiscountCode('');
            flashActionSuccess('welcome');
        } catch (err) {
            const message = err?.status === 401
                ? 'Debes iniciar sesión para aplicar descuentos.'
                : (err?.data?.message || 'No se pudo aplicar el descuento.');
            setGlobalDiscountError(message);
        } finally {
            setApplyingGlobalDiscount(false);
        }
    };

    useEffect(() => {
        const companyDiscounts = discountsForCompany(activeGlobalDiscount, selectedCompanyRuc);
        const currentCode = String(appliedCoupon?.code || '').trim().toUpperCase();
        const previous = previousGlobalDiscountRef.current;
        const previousDiscount = findGlobalDiscountByCode(companyDiscounts, previous?.code);

        if (
            previous?.companyRuc
            && previous.companyRuc !== selectedCompanyRuc
            && globalDiscountError === GLOBAL_DISCOUNT_BELOW_MINIMUM_MESSAGE
        ) {
            setGlobalDiscountError('');
        } else if (
            previous?.code
            && previous.companyRuc === selectedCompanyRuc
            && !currentCode
            && previousDiscount
            && !discountMeetsMinimumPurchase(previousDiscount, preDiscountItemsPrice)
        ) {
            setGlobalDiscountError(GLOBAL_DISCOUNT_BELOW_MINIMUM_MESSAGE);
        } else if (
            previousDiscount
            && discountMeetsMinimumPurchase(previousDiscount, preDiscountItemsPrice)
            && globalDiscountError === GLOBAL_DISCOUNT_BELOW_MINIMUM_MESSAGE
        ) {
            setGlobalDiscountError('');
        }

        const currentDiscount = findGlobalDiscountByCode(companyDiscounts, currentCode);
        if (currentCode && currentDiscount) {
            previousGlobalDiscountRef.current = {
                code: currentCode,
                companyRuc: selectedCompanyRuc,
            };
        }
    }, [
        activeGlobalDiscount,
        appliedCoupon?.code,
        globalDiscountError,
        preDiscountItemsPrice,
        selectedCompanyRuc,
    ]);

    useEffect(() => {
        if (!userInfo || !globalDiscountRemovalKey) {
            globalDiscountRemovalAttemptRef.current = '';
            return undefined;
        }

        if (globalDiscountRemovalAttemptRef.current === globalDiscountRemovalKey) {
            return undefined;
        }

        globalDiscountRemovalAttemptRef.current = globalDiscountRemovalKey;
        const companyRucs = [...new Set(
            globalDiscountRemovalKey.split('|').map((entry) => entry.slice(0, entry.indexOf(':')))
        )].filter(Boolean);
        let cancelled = false;

        (async () => {
            try {
                for (const companyRuc of companyRucs) {
                    if (cancelled) {
                        return;
                    }
                    await removeDiscountFromCart({ companyRuc }).unwrap();
                }
            } catch (err) {
                if (!cancelled) {
                    globalDiscountRemovalAttemptRef.current = '';
                    setGlobalDiscountError(
                        err?.status === 401
                            ? 'Debes iniciar sesión para quitar descuentos.'
                            : (err?.data?.message || 'No se pudo quitar el descuento.')
                    );
                }
            }
        })();

        return () => {
            cancelled = true;
            if (globalDiscountRemovalAttemptRef.current === globalDiscountRemovalKey) {
                globalDiscountRemovalAttemptRef.current = '';
            }
        };
    }, [globalDiscountRemovalKey, removeDiscountFromCart, userInfo]);

    // Check if any item in the selected company has stock errors based on the validation response
    const hasStockIssuesInSelected = selectedCompanyItems.some(item => {
        if (!item) return false;
        // Check if this item is in the stockErrors list
        const errorItem = stockErrors.find(err => err.productId === item.product);
        return !!errorItem;
    });

    const totalQty = allItems.reduce((acc, item) => acc + (item?.qty || 0), 0);
    const activeCompanyIndex = Math.max(
        0,
        companiesArray.findIndex((company) => company.company === selectedCompanyRuc)
    );
    const hasMultipleCompanies = companiesArray.length > 1;
    const mobileFooterHasImportantMessages = Boolean(
        appliedCoupon?.code ||
        hasStockIssuesInSelected ||
        (stockValidationFailed && stockValidationMessage)
    );

    const dismissSwipeHint = useCallback(() => {
        setShowSwipeHint(false);
        try {
            window.localStorage.setItem('cartSwipeHintSeen', '1');
        } catch (error) {
            // Ignore localStorage failures.
        }
    }, []);

    const navigateCompanySlide = useCallback(
        (direction) => {
            dismissSwipeHint();
            if (!swiperRef.current) return;

            if (direction === 'prev') {
                swiperRef.current.slidePrev();
                return;
            }

            swiperRef.current.slideNext();
        },
        [dismissSwipeHint]
    );

    const updateSwiperAutoHeight = useCallback((speed = 0) => {
        const swiper = swiperRef.current;
        if (!swiper || swiper.destroyed || !swiper.params || !swiper.wrapperEl) {
            return;
        }

        try {
            swiper.update();
            if (!swiperRef.current || swiperRef.current.destroyed || !swiperRef.current.params) {
                return;
            }
            if (typeof swiper.updateAutoHeight === 'function') {
                swiper.updateAutoHeight(speed);
            }
        } catch (error) {
            // A destroyed Swiper has no params; ignore the late height update.
        }
    }, []);

    const showMobileCartBar = isMobile && !isLoading && !error && companiesArray.length > 0;

    useEffect(() => {
        const clearCartBarSpace = () => {
            document.documentElement.style.removeProperty('--cart-mobile-bar-height');
            document.body.classList.remove('cart-has-mobile-bar');
        };

        if (!showMobileCartBar) {
            clearCartBarSpace();
            return undefined;
        }

        const footerNode = mobileFooterRef.current;
        if (!footerNode) {
            clearCartBarSpace();
            return undefined;
        }

        document.body.classList.add('cart-has-mobile-bar');

        const measure = () => {
            const footerHeight = Math.ceil(footerNode.getBoundingClientRect().height || 0);
            document.documentElement.style.setProperty('--cart-mobile-bar-height', `${footerHeight}px`);
        };

        measure();

        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(footerNode);
        window.addEventListener('resize', measure);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', measure);
            clearCartBarSpace();
        };
    }, [showMobileCartBar]);

    useEffect(() => {
        let active = true;
        const rafId = window.requestAnimationFrame(() => {
            if (!active) return;
            updateSwiperAutoHeight(150);
        });
        return () => {
            active = false;
            window.cancelAnimationFrame(rafId);
        };
    }, [
        updateSwiperAutoHeight,
        selectedCompanyRuc,
        selectedCompanyItems.length,
        stockErrors.length,
        isValidatingStock,
        hasStockIssuesInSelected,
        discountApplyError,
        hasAppliedDiscount,
        removingCoupon,
        loadingCoupon,
    ]);

    useEffect(() => {
        if (!hasMultipleCompanies) {
            setShowSwipeHint(false);
            return;
        }

        try {
            const hintSeen = window.localStorage.getItem('cartSwipeHintSeen') === '1';
            if (hintSeen) return;

            setShowSwipeHint(true);
            const timerId = window.setTimeout(() => dismissSwipeHint(), 2800);
            return () => window.clearTimeout(timerId);
        } catch (error) {
            setShowSwipeHint(true);
            const timerId = window.setTimeout(() => setShowSwipeHint(false), 2800);
            return () => window.clearTimeout(timerId);
        }
    }, [hasMultipleCompanies, dismissSwipeHint]);

    useEffect(() => {
        if (!isMobile || !showMobileFooterDetails || appliedCoupon?.code || loadingCoupon) {
            return;
        }

        if (discountCode.trim().length > 0) {
            return;
        }

        const timerId = window.setTimeout(() => {
            setShowMobileFooterDetails(false);
            setDiscountApplyError('');
        }, 2600);

        return () => window.clearTimeout(timerId);
    }, [
        isMobile,
        showMobileFooterDetails,
        appliedCoupon?.code,
        loadingCoupon,
        discountCode,
    ]);

    useEffect(() => {
        if (!isMobile || !showMobileFooterDetails || appliedCoupon?.code || !discountApplyError) {
            return;
        }

        const timerId = window.setTimeout(() => {
            setShowMobileFooterDetails(false);
            setDiscountApplyError('');
        }, 1800);

        return () => window.clearTimeout(timerId);
    }, [isMobile, showMobileFooterDetails, appliedCoupon?.code, discountApplyError]);

    if (isLoading) {
        return <Loader />;
    }

    if (error) {
        return <Message variant='danger'>{error?.data?.message || error.error}</Message>;
    }

    if (!activeOrder || companiesArray.length === 0) {
        return (
            <div className="empty-cart-container">
                <Meta title="Carrito de Compras" />
                <div className="empty-cart-icon-wrapper">
                    <FaShoppingCart size={50} />
                </div>
                <h2 className="empty-cart-title">Tu carrito está vacío</h2>
                <p className="empty-cart-text">
                    Parece que aún no has añadido ningún producto. ¡Explora nuestro catálogo y encuentra lo que necesitas!
                </p>
                <Link to="/" className="btn btn-primary btn-start-shopping">
                    Empezar a comprar
                </Link>
            </div>
        );
    }

    const initialSlide = Math.max(0, companiesArray.findIndex(c => c.company === selectedCompanyRuc));

    const renderCompanyNavigation = ({ inMobileFooter = false } = {}) => (
        <div className={inMobileFooter ? 'cart-company-nav-mobile-footer mb-2' : 'cart-company-nav-sticky mb-3'}>
            <div className="cart-company-nav" aria-label="Navegación entre compañías">
                <button
                    type="button"
                    className="cart-company-nav-btn cart-nav-prev"
                    aria-label="Empresa anterior"
                    disabled={activeCompanyIndex <= 0}
                    onClick={() => navigateCompanySlide('prev')}
                >
                    <FaChevronLeft aria-hidden="true" />
                    <span>Anterior</span>
                </button>

                <div className="cart-company-nav-status" aria-live="polite">
                    <span className="cart-company-nav-label">Tienda</span>
                    <strong>
                        {activeCompanyIndex + 1} de {companiesArray.length}
                    </strong>
                </div>

                <button
                    type="button"
                    className="cart-company-nav-btn cart-nav-next"
                    aria-label="Siguiente empresa"
                    disabled={activeCompanyIndex >= companiesArray.length - 1}
                    onClick={() => navigateCompanySlide('next')}
                >
                    <span>Siguiente</span>
                    <FaChevronRight aria-hidden="true" />
                </button>
            </div>

            {showSwipeHint && !inMobileFooter && (
                <div className="cart-swipe-hint" role="status" aria-live="polite">
                    Desliza para cambiar de tienda
                </div>
            )}
        </div>
    );

    return (
        <>
            <Meta title="Carrito de Compras" />
            <Row>
                <Col xs={12} md={8} xl={9}>
                    <h1 className="responsive-title mb-4">Carrito de Compras</h1>

                    {hasMultipleCompanies && (
                        <div className="mb-4 d-flex align-items-center gap-2 flex-wrap">
                            <Badge bg="primary" pill className="px-3 py-2 fs-6">
                                <FaInfoCircle className="me-2" />
                                Estás comprando en {companiesArray.length} tiendas diferentes
                            </Badge>
                            <small className="text-muted ms-2">
                                <FaArrowRight className="me-1" /> Desliza o usa los controles para
                                cambiar de tienda
                            </small>
                        </div>
                    )}

                    {hasMultipleCompanies && !isMobile && renderCompanyNavigation()}

                    <div>
                        <div className="cart-swiper-shell">
                            <span
                                className={`cart-swipe-edge cart-swipe-edge-left ${activeCompanyIndex <= 0 ? 'is-hidden' : ''}`}
                                aria-hidden="true"
                            />
                            <span
                                className={`cart-swipe-edge cart-swipe-edge-right ${activeCompanyIndex >= companiesArray.length - 1 ? 'is-hidden' : ''}`}
                                aria-hidden="true"
                            />

                            <Swiper
                                modules={[Pagination]}
                                spaceBetween={20}
                                slidesPerView={1}
                                pagination={{ clickable: true }}
                                initialSlide={initialSlide}
                                onSlideChange={handleSlideChange}
                                autoHeight={true}
                                observer={true}
                                observeParents={true}
                                watchSlidesProgress={true}
                                onSwiper={(swiper) => {
                                    swiperRef.current = swiper;
                                    swiper.on('destroy', () => {
                                        if (swiperRef.current === swiper) {
                                            swiperRef.current = null;
                                        }
                                    });
                                }}
                                className="cart-swiper pb-5"
                            >
                                {companiesArray.map((companyOrder) => {
                                    const {
                                        company: companyRuc,
                                        shippingPrice,
                                        taxPrice,
                                        totalPrice,
                                        isPaid,
                                        checkoutLocked,
                                    } = companyOrder;
                                    const orderItems =
                                        companyOrder.orderItems || companyOrder.items || [];
                                    const companyInfo = companiesByRuc?.[companyRuc] || {
                                        ruc: companyRuc,
                                        tradeName: 'Comercial Santy',
                                    };
                                    const isReadOnly = isPaid || checkoutLocked;
                                    const isSelected = selectedCompanyRuc === companyRuc;
                                    const canSelect = !isPaid && !checkoutLocked;
                                    const companyDiscountAmount = Number(
                                        companyOrder?.discountAmount ??
                                            companyOrder?.couponDiscountAmount ??
                                            0
                                    );
                                    const companyPreDiscountItemsPrice = getExTaxItemsSubtotal(orderItems);
                                    const companyCartTotals = totalsWithoutShipping({
                                        totalPrice,
                                        taxPrice,
                                        shippingPrice,
                                    });

                                    return (
                                        <SwiperSlide key={companyRuc}>
                                            <div
                                                className={`company-group-container ${isSelected ? 'selected-company border-primary shadow-sm' : 'border'}`}
                                                onClick={() =>
                                                    canSelect &&
                                                    dispatch(setSelectedCompany(companyRuc))
                                                }
                                                style={{
                                                    cursor: canSelect ? 'pointer' : 'default',
                                                    borderWidth: isSelected ? '2px' : '1px',
                                                    borderRadius: '0.5rem',
                                                    transition: 'all 0.2s ease-in-out',
                                                    backgroundColor: isSelected
                                                        ? '#fff5f2'
                                                        : '#fff',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {/* Loading Overlay for Validation */}
                                                {isSelected && isValidatingStock && (
                                                    <div
                                                        className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                                                        style={{
                                                            backgroundColor:
                                                                'rgba(255, 255, 255, 0.7)',
                                                            backdropFilter: 'blur(2px)',
                                                            zIndex: 10,
                                                        }}
                                                    >
                                                        <Spinner
                                                            animation="border"
                                                            variant="primary"
                                                        />
                                                    </div>
                                                )}

                                                <div className="p-3">
                                                    <div className="d-flex align-items-center gap-3 mb-3 border-bottom pb-2">
                                                        {canSelect && (
                                                            <Form.Check
                                                                type="radio"
                                                                name="selectedCompany"
                                                                id={`company-${companyRuc}`}
                                                                checked={isSelected}
                                                                onChange={() =>
                                                                    dispatch(
                                                                        setSelectedCompany(
                                                                            companyRuc
                                                                        )
                                                                    )
                                                                }
                                                                className="company-selector-radio"
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        )}
                                                        {companyInfo.logoUrl && (
                                                            <Image
                                                                src={companyInfo.logoUrl}
                                                                alt={companyInfo.tradeName}
                                                                className="company-logo-cart"
                                                            />
                                                        )}
                                                        <div className="d-flex flex-column">
                                                            <h3 className="fs-5 fw-bold mb-0 text-primary">
                                                                Vendido por: {companyInfo.tradeName}
                                                            </h3>
                                                            {companyInfo.city && (
                                                                <small className="text-muted">
                                                                    Ciudad: {companyInfo.city}
                                                                </small>
                                                            )}
                                                        </div>

                                                        <div className="cart-company-actions d-flex align-items-center gap-2 ms-auto">
                                                            {!isReadOnly && (
                                                                <Button
                                                                    type="button"
                                                                    size="sm"
                                                                    variant="outline-primary"
                                                                    className={`cart-empty-company-btn ${confirmEmptyRuc === companyRuc ? 'is-confirm' : ''} ${feedbackClassName(actionFeedback[`empty:${companyRuc}`])}`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleEmptyCompanyCart(companyRuc);
                                                                    }}
                                                                    disabled={Boolean(emptyingRuc)}
                                                                    aria-label={
                                                                        confirmEmptyRuc === companyRuc
                                                                            ? `Confirmar vaciar carrito de ${companyInfo.tradeName}`
                                                                            : `Vaciar carrito de ${companyInfo.tradeName}`
                                                                    }
                                                                >
                                                                    {emptyingRuc === companyRuc ? (
                                                                        <Spinner animation="border" size="sm" />
                                                                    ) : actionFeedback[`empty:${companyRuc}`] === 'success' ? (
                                                                        <FaCheck size={12} />
                                                                    ) : (
                                                                        confirmEmptyRuc === companyRuc ? '¿Vaciar?' : 'Vaciar'
                                                                    )}
                                                                </Button>
                                                            )}
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant="outline-primary"
                                                                className={`rounded-circle d-inline-flex align-items-center justify-content-center ${feedbackClassName(actionFeedback[`share:${companyRuc}`])} ${actionFeedback[`share:${companyRuc}`] === 'success' ? 'is-added' : ''}`}
                                                                style={{ width: 36, height: 36, padding: 0, borderColor: '#b5430f', color: '#b5430f' }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleShareCompanyCart(companyRuc);
                                                                }}
                                                                disabled={isCreatingSharedCart}
                                                                aria-label="Compartir carrito de esta empresa"
                                                                title="Compartir"
                                                            >
                                                                {actionFeedback[`share:${companyRuc}`] === 'success' ? (
                                                                    <FaCheck size={12} />
                                                                ) : (
                                                                    <FaShareAlt size={12} />
                                                                )}
                                                            </Button>
                                                            {companyOrder.isLocalShipping && (
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip
                                                                            id={`tooltip-local-${companyRuc}`}
                                                                        >
                                                                            Esta compañía aplica
                                                                            tarifa local ('P') para
                                                                            esta ciudad.
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <Badge
                                                                        bg="info"
                                                                        className="status-badge-cart"
                                                                    >
                                                                        Tarifa local
                                                                    </Badge>
                                                                </OverlayTrigger>
                                                            )}
                                                            {isPaid && (
                                                                <Badge
                                                                    bg="success"
                                                                    className="status-badge-cart"
                                                                >
                                                                    Pagado
                                                                </Badge>
                                                            )}
                                                            {checkoutLocked && !isPaid && (
                                                                <div className="mt-1">
                                                                    <Badge
                                                                        bg="warning"
                                                                        text="dark"
                                                                        className="status-badge-cart"
                                                                    >
                                                                        Pago en proceso
                                                                    </Badge>
                                                                    <div className="text-warning small fw-bold mt-1">
                                                                        <FaLock className="me-1" />{' '}
                                                                        Pago en proceso para esta
                                                                        compañía
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <ListGroup variant="flush">
                                                        {orderItems.map((item) => {
                                                            if (!item) return null;

                                                            // Check if this item has a stock error from validation
                                                            const stockError = stockErrors.find(
                                                                (err) =>
                                                                    err.productId === item.product
                                                            );
                                                            const isOutOfStock = !!stockError;

                                                            return (
                                                                <ListGroup.Item
                                                                    key={item._id}
                                                                    className={`py-4 px-0 px-md-2 cart-item-row bg-transparent ${isOutOfStock ? 'opacity-75' : ''}`}
                                                                >
                                                                    <Row className="align-items-center gx-3 gy-2 gy-md-3">
                                                                        <Col xs={3} md={2} lg={1}>
                                                                            <div className="position-relative">
                                                                                <Image
                                                                                    src={
                                                                                        item.image ||
                                                                                        '/images/default.jpg'
                                                                                    }
                                                                                    onError={(
                                                                                        e
                                                                                    ) => {
                                                                                        e.target.src =
                                                                                            '/images/default.jpg';
                                                                                    }}
                                                                                    onLoad={() =>
                                                                                        updateSwiperAutoHeight()
                                                                                    }
                                                                                    fluid
                                                                                    rounded
                                                                                    alt={item.name}
                                                                                    className="cart-item-image"
                                                                                    style={{
                                                                                        filter: isOutOfStock
                                                                                            ? 'grayscale(100%)'
                                                                                            : 'none',
                                                                                    }}
                                                                                />
                                                                                {isOutOfStock && (
                                                                                    <div className="position-absolute top-50 start-50 translate-middle w-100 text-center">
                                                                                        <Badge
                                                                                            bg="danger"
                                                                                            className="shadow-sm"
                                                                                        >
                                                                                            AGOTADO
                                                                                        </Badge>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </Col>
                                                                        <Col xs={9} md={10} lg={4}>
                                                                            <div className="d-flex flex-column h-100">
                                                                                <Link
                                                                                    to={`/product/${item.product}`}
                                                                                    className="fw-bold text-dark text-decoration-none mb-1 cart-item-name"
                                                                                    onClick={(e) =>
                                                                                        e.stopPropagation()
                                                                                    }
                                                                                >
                                                                                    {item.name}
                                                                                </Link>
                                                                                <FreeShippingBadge product={item} companyRuc={companyRuc} />
                                                                                <ProductRowBenefitNotes product={item} companyRuc={companyRuc} />
                                                                                <ContificoCodeBadge
                                                                                    code={
                                                                                        item?.contificoCode
                                                                                    }
                                                                                    compact
                                                                                    className="mb-2"
                                                                                />

                                                                                {isOutOfStock ? (
                                                                                    <div className="text-danger small fw-bold mb-1 d-flex align-items-center">
                                                                                        <FaInfoCircle className="me-1" />
                                                                                        {stockError.message ||
                                                                                            'Producto agotado o insuficiente'}
                                                                                    </div>
                                                                                ) : null}

                                                                                <CartProductUrgency
                                                                                    productId={
                                                                                        item.product
                                                                                    }
                                                                                />

                                                                                {userInfo?.priceAssigned ===
                                                                                    'pvp_distribuidor' && (
                                                                                    <CartDistributorStockInfo
                                                                                        productId={
                                                                                            item.product
                                                                                        }
                                                                                    />
                                                                                )}

                                                                                <div
                                                                                    className={`cart-item-variations mt-1 ${feedbackClassName(actionFeedback[`variation:${item._id}`])}`}
                                                                                    onClick={(e) =>
                                                                                        e.stopPropagation()
                                                                                    }
                                                                                >
                                                                                    <CartVariationSelector
                                                                                        item={item}
                                                                                        onChange={
                                                                                            handleVariationChange
                                                                                        }
                                                                                        disabled={
                                                                                            isReadOnly
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </Col>
                                                                        <Col
                                                                            xs={12}
                                                                            md={12}
                                                                            lg={7}
                                                                            className="mt-3 mt-lg-0 px-3 px-md-2"
                                                                        >
                                                                            <Row className="align-items-center g-3">
                                                                                <Col
                                                                                    xs={5}
                                                                                    md={4}
                                                                                    lg={4}
                                                                                    className="d-flex justify-content-center"
                                                                                    onClick={(e) =>
                                                                                        e.stopPropagation()
                                                                                    }
                                                                                >
                                                                                    <div className={feedbackClassName(actionFeedback[`qty:${item._id}`])}>
                                                                                    <QuantitySelector
                                                                                        value={
                                                                                            item.qty
                                                                                        }
                                                                                        // If we don't have countInStock, we rely on validation.
                                                                                        // We can set a high max or keep it as is if we had it.
                                                                                        // Assuming we might not have it, we let user try to update,
                                                                                        // and validation will catch it.
                                                                                        max={
                                                                                            item.countInStock ||
                                                                                            999
                                                                                        }
                                                                                        onChange={(
                                                                                            val
                                                                                        ) =>
                                                                                            handleQtyChange(
                                                                                                item,
                                                                                                val
                                                                                            )
                                                                                        }
                                                                                        size="sm"
                                                                                        debounceMs={
                                                                                            500
                                                                                        }
                                                                                        disabled={
                                                                                            isReadOnly ||
                                                                                            isOutOfStock
                                                                                        }
                                                                                        allowDecrease={
                                                                                            isOutOfStock
                                                                                        }
                                                                                    />
                                                                                    </div>
                                                                                </Col>
                                                                                <Col
                                                                                    xs={5}
                                                                                    md={6}
                                                                                    lg={6}
                                                                                    className="text-center px-0 px-md-2"
                                                                                >
                                                                                    <ProductPrice
                                                                                        product={
                                                                                            item
                                                                                        }
                                                                                        userInfo={
                                                                                            userInfo
                                                                                        }
                                                                                        qty={
                                                                                            item.qty
                                                                                        }
                                                                                        size="md"
                                                                                        className="justify-content-center"
                                                                                    />
                                                                                    {(() => {
                                                                                        const productDiscount =
                                                                                            Number(
                                                                                                item?.discount ||
                                                                                                    0
                                                                                            );
                                                                                        const couponItemDiscount =
                                                                                            Number(
                                                                                                item?.couponDiscountAmount ||
                                                                                                    item?.couponDiscount ||
                                                                                                    0
                                                                                            );
                                                                                        return (
                                                                                            couponItemDiscount >
                                                                                                0 &&
                                                                                            productDiscount >
                                                                                                0 &&
                                                                                            productDiscount >
                                                                                                couponItemDiscount
                                                                                        );
                                                                                    })() && (
                                                                                        <Badge
                                                                                            bg="info"
                                                                                            className="mt-1"
                                                                                        >
                                                                                            Mayor
                                                                                            descuento
                                                                                            aplicado
                                                                                        </Badge>
                                                                                    )}
                                                                                </Col>
                                                                                <Col
                                                                                    xs={2}
                                                                                    md={2}
                                                                                    lg={2}
                                                                                    className="text-end"
                                                                                    onClick={(e) =>
                                                                                        e.stopPropagation()
                                                                                    }
                                                                                >
                                                                                    <Button
                                                                                        type="button"
                                                                                        variant="light"
                                                                                        size="sm"
                                                                                        className={`text-danger cart-remove-btn ${feedbackClassName(actionFeedback[`remove:${item._id}`])} ${actionFeedback[`remove:${item._id}`] === 'success' ? 'is-added' : ''}`}
                                                                                        onClick={() =>
                                                                                            handleRemoveItem(
                                                                                                item._id
                                                                                            )
                                                                                        }
                                                                                        aria-label={`Eliminar ${item.name}`}
                                                                                        disabled={
                                                                                            isReadOnly
                                                                                        }
                                                                                    >
                                                                                        {actionFeedback[`remove:${item._id}`] === 'success' ? <FaCheck /> : <FaTrash />}
                                                                                    </Button>
                                                                                </Col>
                                                                            </Row>
                                                                        </Col>
                                                                    </Row>
                                                                </ListGroup.Item>
                                                            );
                                                        })}
                                                    </ListGroup>

                                                    <ShippingBehaviorBanner
                                                        companyRuc={companyRuc}
                                                        policy={companyInfo.shippingPolicy || companyOrder.shippingPolicySnapshot}
                                                        companyName={companyInfo.tradeName}
                                                        className="mt-3 mb-0"
                                                    />
                                                    <div className="p-3 rounded mt-3 company-summary-box shadow-sm bg-white">
                                                        <OrderPriceSummary
                                                            dense
                                                            subtotal={companyPreDiscountItemsPrice}
                                                            discountAmount={companyDiscountAmount}
                                                            discount={resolveSummaryDiscount({
                                                                activeGlobalDiscount,
                                                                companyRuc,
                                                                appliedCoupon: companyOrder?.appliedCoupon
                                                                    || (companyRuc === selectedCompanyRuc ? appliedCoupon : null),
                                                            })}
                                                            shippingText="Por calcular"
                                                            taxPrice={companyCartTotals.taxPrice}
                                                            total={(
                                                                <TotalPrice
                                                                    price={companyCartTotals.totalPrice}
                                                                    size="lg"
                                                                    className="cart-company-total"
                                                                />
                                                            )}
                                                            afterDiscount={companyDiscountAmount > 0 ? null : (
                                                                <ActiveGlobalDiscountLine
                                                                    companyRuc={companyRuc}
                                                                    appliedCode={companyOrder?.appliedCoupon?.code}
                                                                    amount={companyDiscountAmount}
                                                                />
                                                            )}
                                                        />
                                                        <div className="mt-2 pt-2 border-top shipping-estimate-note">
                                                            <small
                                                                className="text-muted d-block"
                                                                style={{
                                                                    fontSize: '0.7rem',
                                                                    lineHeight: '1.3',
                                                                }}
                                                            >
                                                                <FaInfoCircle
                                                                    className="me-1 text-info"
                                                                    size={10}
                                                                />
                                                                El costo final de envío se
                                                                calculará en el siguiente paso
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </SwiperSlide>
                                    );
                                })}
                            </Swiper>
                        </div>
                    </div>
                </Col>

                <Col xs={12} md={4} xl={3} className="mt-4 mt-md-0 d-none d-md-block">
                    <div className="cart-summary-sticky">
                        <Card className="shadow-sm border-0">
                            <ListGroup variant="flush">
                                <ListGroup.Item className="py-3 cart-summary-total-panel">
                                    <Form onSubmit={handleApplyDiscount}>
                                        <Form.Label
                                            column={false}
                                            className="small fw-semibold mb-2"
                                            style={{ color: '#b5430f' }}
                                        >
                                            Código de descuento
                                        </Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="text"
                                                placeholder="Ingrese código de descuento"
                                                value={discountCode}
                                                onChange={(e) => setDiscountCode(e.target.value)}
                                                disabled={loadingCoupon || hasAppliedDiscount}
                                            />
                                            <Button
                                                type="submit"
                                                variant="outline-primary"
                                                disabled={loadingCoupon || hasAppliedDiscount}
                                            >
                                                {loadingCoupon ? 'Aplicando...' : 'Aplicar'}
                                            </Button>
                                        </InputGroup>
                                    </Form>
                                    {!selectedCompanyRuc ? (
                                        <small className="text-muted d-block mt-2">
                                            Selecciona una tienda para aplicar cupón.
                                        </small>
                                    ) : null}
                                    {discountApplyError ? (
                                        <Alert variant="danger" className="mt-2 mb-0">
                                            {discountApplyError}
                                        </Alert>
                                    ) : null}
                                    {globalDiscountNotice ? (
                                        <Alert variant="warning" className="mt-2 mb-0">
                                            {globalDiscountNotice}
                                        </Alert>
                                    ) : null}
                                </ListGroup.Item>

                                {appliedCoupon?.code ? (
                                    <ListGroup.Item className="py-2">
                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                            <span className="text-success fw-semibold">
                                                Cupón aplicado: {appliedCoupon.code}
                                            </span>
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline-danger"
                                                onClick={handleRemoveDiscount}
                                                disabled={removingCoupon}
                                            >
                                                {removingCoupon ? 'Quitando...' : 'Quitar cupón'}
                                            </Button>
                                        </div>
                                    </ListGroup.Item>
                                ) : null}

                                <ListGroup.Item className="py-3">
                                    <h2 className="fs-5 fw-bold mb-3">
                                        {selectedCompany ? 'Total a Pagar' : 'Resumen de Compra'}
                                    </h2>
                                    <OrderPriceSummary
                                        dense
                                        showTotal={false}
                                        subtotal={preDiscountItemsPrice}
                                        discountAmount={discountAmount}
                                        discount={summaryDiscount}
                                        shippingText="Por calcular"
                                        taxPrice={selectedCartTotals.taxPrice}
                                        afterDiscount={(
                                            <>
                                                {discountAmount > 0 ? null : (
                                                    <ActiveGlobalDiscountLine
                                                        companyRuc={selectedCompanyRuc}
                                                        appliedCode={appliedCoupon?.code}
                                                        amount={discountAmount}
                                                    />
                                                )}
                                                <ApplyGlobalDiscountButton
                                                    discount={welcomeButtonDiscount}
                                                    succeeded={actionFeedback.welcome === 'success'}
                                                    onApply={handleApplyGlobalDiscount}
                                                    applying={applyingGlobalDiscount}
                                                    disabled={loadingCoupon}
                                                    companyName={companiesArray.length > 1 ? selectedCompanyName : ''}
                                                />
                                            </>
                                        )}
                                    />
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <span className="text-muted small">
                                            {selectedCompany
                                                ? 'Tienda seleccionada:'
                                                : `Total (${totalQty} ${totalQty === 1 ? 'ítem' : 'ítems'})`}
                                        </span>
                                    </div>
                                    <div className="mb-2">
                                        <TotalPrice
                                            price={
                                                selectedCompany
                                                    ? selectedCartTotals.totalPrice
                                                    : activeOrder.totalPrice
                                            }
                                            size="xl"
                                            className="cart-summary-total-value"
                                        />
                                    </div>
                                    <small
                                        className="text-muted d-block"
                                        style={{ fontSize: '0.75rem', lineHeight: '1.4' }}
                                    >
                                        {selectedCompany ? (
                                            <>
                                                (Incluye IVA)
                                                <span className="d-block mt-1 text-info">
                                                    <FaInfoCircle size={10} className="me-1" />
                                                    Envío final se calculará en el siguiente
                                                    paso
                                                </span>
                                            </>
                                        ) : (
                                            '(Todos los precios incluyen IVA)'
                                        )}
                                    </small>
                                </ListGroup.Item>

                                <ListGroup.Item className="py-3 bg-light">
                                    {hasStockIssuesInSelected && (
                                        <div className="text-danger small fw-bold mb-3 d-flex align-items-start">
                                            <FaInfoCircle className="me-2" />
                                            <span>Hay productos con problemas de stock.</span>
                                        </div>
                                    )}

                                    {stockValidationFailed && stockValidationMessage && (
                                        <Alert variant="danger" className="mb-3">
                                            {stockValidationMessage}
                                        </Alert>
                                    )}

                                    <Button
                                        type="button"
                                        className={`btn-block w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm ${feedbackClassName(actionFeedback.checkout)}`}
                                        disabled={
                                            !selectedCompany ||
                                            hasStockIssuesInSelected ||
                                            stockValidationFailed ||
                                            isValidatingStock ||
                                            selectedGlobalDiscountBelowMinimum
                                        }
                                        onClick={checkoutHandler}
                                        variant="primary"
                                    >
                                        {isValidatingStock
                                            ? 'Verificando...'
                                            : userInfo?.priceAssigned === 'pvp_distribuidor'
                                              ? 'Cotizar'
                                              : 'Ir a Pagar'}{' '}
                                        <FaArrowRight
                                            className={isValidatingStock ? 'd-none' : ''}
                                        />
                                    </Button>
                                    {userInfo?.priceAssigned !== 'pvp_distribuidor' && (
                                        <div className="text-center mt-3">
                                            <small
                                                className="text-muted d-flex align-items-center justify-content-center"
                                                style={{ fontSize: '0.7rem' }}
                                            >
                                                <FaLock className="me-2 text-success" /> Pago 100%
                                                Seguro
                                            </small>
                                        </div>
                                    )}
                                </ListGroup.Item>
                            </ListGroup>
                        </Card>
                    </div>
                </Col>
            </Row>

            <div
                ref={mobileFooterRef}
                className="d-md-none cart-footer checkout-mobile-bar"
            >
                <div className="cart-footer-inner">
                    <div className="checkout-mobile-bar-summary">
                        <div>
                            <span className="checkout-mobile-bar-label">
                                {selectedCompany
                                    ? 'Total a pagar'
                                    : `Total (${totalQty} ${totalQty === 1 ? 'ítem' : 'ítems'})`}
                            </span>
                            <TotalPrice
                                price={
                                    selectedCompany
                                        ? selectedCartTotals.totalPrice
                                        : activeOrder.totalPrice
                                }
                                size="xl"
                                className="justify-content-start cart-mobile-total-value"
                            />
                        </div>
                        <div className="checkout-mobile-bar-meta">
                            <span>IVA incluido</span>
                            {discountAmount > 0 && (
                                <span className="text-success fw-semibold">
                                    {discountLabel} -${discountAmount.toFixed(2)}
                                </span>
                            )}
                        </div>
                    </div>

                    <ApplyGlobalDiscountButton
                        discount={welcomeButtonDiscount}
                                        succeeded={actionFeedback.welcome === 'success'}
                        onApply={handleApplyGlobalDiscount}
                        applying={applyingGlobalDiscount}
                        disabled={loadingCoupon}
                        companyName={companiesArray.length > 1 ? selectedCompanyName : ''}
                        showLabel
                    />
                    {globalDiscountNotice ? (
                        <Alert variant="warning" className="mt-2 mb-0 py-2 small">
                            {globalDiscountNotice}
                        </Alert>
                    ) : null}

                    {(showMobileFooterDetails || mobileFooterHasImportantMessages) && (
                        <div className="cart-mobile-details-panel">
                            {appliedCoupon?.code ? (
                                <div className="cart-mobile-coupon-applied">
                                    <div className="cart-mobile-coupon-badge">
                                        <FaTag size={14} className="me-1" aria-hidden="true" />
                                        <span className="fw-semibold">{appliedCoupon.code}</span>
                                    </div>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline-danger"
                                        className="cart-mobile-remove-coupon-btn"
                                        onClick={handleRemoveDiscount}
                                        disabled={removingCoupon}
                                    >
                                        {removingCoupon ? 'Quitando...' : 'Quitar'}
                                    </Button>
                                </div>
                            ) : (
                                <div className="mb-2 p-2 rounded cart-mobile-coupon-box">
                                    <Form onSubmit={handleApplyDiscount}>
                                        <Form.Label
                                            column={false}
                                            className="small fw-semibold mb-2"
                                            style={{ color: '#b5430f' }}
                                        >
                                            Código de descuento
                                        </Form.Label>
                                        <InputGroup size="sm">
                                            <Form.Control
                                                type="text"
                                                placeholder="Ingrese código"
                                                value={discountCode}
                                                onChange={(e) => setDiscountCode(e.target.value)}
                                                disabled={loadingCoupon}
                                            />
                                            <Button
                                                type="submit"
                                                variant="outline-primary"
                                                disabled={loadingCoupon}
                                            >
                                                {loadingCoupon ? 'Aplicando...' : 'Aplicar'}
                                            </Button>
                                        </InputGroup>
                                    </Form>

                                    {discountApplyError ? (
                                        <Alert variant="danger" className="mt-2 mb-0 py-2 small">
                                            {discountApplyError}
                                        </Alert>
                                    ) : null}
                                </div>
                            )}

                            {hasStockIssuesInSelected && (
                                <div className="text-danger small fw-bold mb-2 d-flex align-items-center justify-content-center">
                                    <FaInfoCircle className="me-2" />
                                    Problemas de stock en la empresa seleccionada
                                </div>
                            )}

                            {stockValidationFailed && stockValidationMessage && (
                                <Alert variant="danger" className="mb-2 py-2 small">
                                    {stockValidationMessage}
                                </Alert>
                            )}
                        </div>
                    )}

                    <div className="checkout-mobile-bar-links">
                        <Button
                            type="button"
                            variant="link"
                            className="cart-footer-details-toggle"
                            onClick={() => setShowMobileFooterDetails((prev) => !prev)}
                            aria-expanded={showMobileFooterDetails || mobileFooterHasImportantMessages}
                        >
                            <FaTag size={13} aria-hidden="true" />
                            <span>
                                {showMobileFooterDetails || mobileFooterHasImportantMessages
                                    ? 'Ocultar cupón'
                                    : '¿Tienes un cupón?'}
                            </span>
                        </Button>
                        {userInfo?.priceAssigned !== 'pvp_distribuidor' && (
                            <span className="checkout-mobile-bar-secure">
                                <FaLock className="me-1 text-success" /> Pago seguro
                            </span>
                        )}
                    </div>

                    <Button
                        type="button"
                        className={`checkout-mobile-bar-action ${feedbackClassName(actionFeedback.checkout)}`}
                        disabled={
                            !selectedCompany ||
                            hasStockIssuesInSelected ||
                            stockValidationFailed ||
                            isValidatingStock ||
                            selectedGlobalDiscountBelowMinimum
                        }
                        onClick={checkoutHandler}
                        variant="primary"
                    >
                        {isValidatingStock
                            ? 'Verificando...'
                            : userInfo?.priceAssigned === 'pvp_distribuidor'
                              ? 'Cotizar'
                              : 'Ir a Pagar'}{' '}
                        <FaArrowRight className={isValidatingStock ? 'd-none' : ''} />
                    </Button>
                </div>
            </div>
            <Modal
                show={showShareModal}
                onHide={() => {
                    setShowShareModal(false);
                    setCopiedShareLink(false);
                }}
                centered
                size="lg"
            >
                <Modal.Header closeButton style={{ borderBottom: '1px solid #fde3d8' }}>
                <Modal.Title className="fw-bold" style={{ color: '#b5430f' }}>Compartir carrito</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="p-3 p-md-4 rounded-3" style={{ background: 'linear-gradient(180deg, #fff8f5 0%, #fff 100%)', border: '1px solid #fde3d8' }}>
                        <p className="mb-3 text-muted">Comparte este enlace con tu cliente o equipo:</p>
                        <div className="d-flex flex-column flex-md-row gap-2 align-items-stretch">
                            <Form.Control
                                readOnly
                                value={sharedCartLink}
                                className="flex-grow-1"
                                style={{ borderColor: '#f2c5b2' }}
                            />
                            <Button
                                variant={copiedShareLink ? 'success' : 'outline-primary'}
                                onClick={handleCopyShareLink}
                                disabled={!sharedCartLink}
                                className={`px-3 fw-semibold ${feedbackClassName(actionFeedback.copy)}`}
                                style={!copiedShareLink ? { borderColor: '#b5430f', color: '#b5430f' } : undefined}
                            >
                                {copiedShareLink ? (
                                    <>
                                        <FaCheckCircle className="me-2" />
                                        Copiado
                                    </>
                                ) : (
                                    'Copiar enlace'
                                )}
                            </Button>
                        </div>
                        <div
                            className="mt-3 d-flex align-items-center gap-2"
                            style={{
                                minHeight: 24,
                                opacity: copiedShareLink ? 1 : 0.65,
                                transition: 'opacity 0.2s ease-in-out',
                                color: copiedShareLink ? '#198754' : '#6c757d',
                            }}
                        >
                            <FaCheckCircle size={14} />
                            <small className="fw-semibold">
                                {copiedShareLink
                                    ? 'Enlace copiado al portapapeles'
                                    : 'Copia el enlace para compartir este carrito'}
                            </small>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowShareModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CartScreen;
